import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, client, unwrap } from "@/api/client";
import { t } from "@/i18n";
import type { TemplateInput } from "./model";

/**
 * The template manager's reads and writes.
 *
 * Every key starts with `templates`, the composer's too, so a template approved
 * or deleted here is gone from the picker in the same breath.
 */

/** Every template, whatever Meta said about it. Management only. */
export function useManagedTemplates() {
  return useQuery({
    queryKey: ["templates", "managed"],
    queryFn: () =>
      unwrap(client.GET("/api/whatsapp/templates/"), t("errors.templates")),
  });
}

/** Create (to Meta for review) without an id; change with one. */
export function useSaveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: TemplateInput }) =>
      id === undefined
        ? unwrap(
            client.POST("/api/whatsapp/templates/", { body }),
            t("errors.saveTemplate"),
          )
        : unwrap(
            client.PUT("/api/whatsapp/templates/{id}/", {
              params: { path: { id } },
              body,
            }),
            t("errors.saveTemplate"),
          ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });
}

/** Deleted in Meta and here. A 204 has no body, so `unwrap` cannot read it. */
export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { error, response } = await client.DELETE("/api/whatsapp/templates/{id}/", {
        params: { path: { id } },
      });
      if (!response.ok) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new ApiError(detail ?? t("errors.deleteTemplate"), response.status);
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });
}

/** Bring every status back from Meta now, rather than at the nightly sync. */
export function useSyncTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(client.POST("/api/whatsapp/templates/sync/"), t("errors.syncTemplates")),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });
}
